import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getWedding } from "../api/weddings";
import { confirmRsvp } from "../api/rsvp";
import { createCheckout } from "../api/payments";
import { getMyRequest } from "../api/requests";
import {
  Wedding,
  GuestRequest,
  PlusOneEntry,
  TicketTier,
  DRESS_CODE_LABELS,
  VIBE_TAG_LABELS,
  TICKET_TIER_LABELS,
  TICKET_TIER_DESCRIPTIONS,
} from "../types";
import { formatDate, formatPhoneInput, formatPrice, isValidPhone, PHONE_PLACEHOLDER } from "../utils/format";
import {
  ghostTierPrice,
  partyTierPrice,
  tierPriceForGuest,
  tierTracksCapacity,
  weddingRequiresPayment,
} from "../utils/tiers";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";
import RsvpForm from "../components/RsvpForm";
import RequestForm from "../components/RequestForm";
import PlusOneForm from "../components/PlusOneForm";

type RsvpStep = "match" | "confirm" | "plus_ones" | "done" | "already_confirmed";

const ProseBlocks: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const blocks = text
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
  if (!blocks.length) return null;
  return (
    <>
      {blocks.map((block, i) => (
        <p key={i} className={className} style={{ whiteSpace: "pre-wrap" }}>
          {block}
        </p>
      ))}
    </>
  );
};

const WeddingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [wedding, setWedding] = useState<Wedding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rsvpId, setRsvpId] = useState<string | null>(null);
  const [ticketTier, setTicketTier] = useState<TicketTier>("party_not_presents");
  const [plusOneLimit, setPlusOneLimit] = useState(0);
  const [rsvpStep, setRsvpStep] = useState<RsvpStep>("match");
  const [myRequest, setMyRequest] = useState<GuestRequest | null>(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState("");
  const [rsvpSuccess, setRsvpSuccess] = useState("");
  const [activeTab, setActiveTab] = useState<"rsvp" | "request">("rsvp");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await getWedding(id);
        setWedding(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Wedding not found");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!id || !user) {
      setMyRequest(null);
      return;
    }
    getMyRequest(id)
      .then(({ data }) => setMyRequest(data))
      .catch(() => setMyRequest(null));
  }, [id, user]);

  const goToManageRsvp = (manageId: string, notice?: string | null) => {
    if (!id) return;
    navigate(`/weddings/${id}/rsvp/${manageId}`, notice ? { state: { notice } } : undefined);
  };

  const handleMatch = (
    guestId: string,
    matchedRsvpId: string,
    tier: TicketTier,
    limit: number,
    status: string,
    extras?: { is_plus_one?: boolean; manage_rsvp_id?: string | null }
  ) => {
    if (extras?.is_plus_one && extras.manage_rsvp_id && id) {
      navigate(
        `/weddings/${id}/rsvp/${extras.manage_rsvp_id}?seat=${encodeURIComponent(guestId)}`
      );
      return;
    }

    setRsvpId(matchedRsvpId);
    setTicketTier(tier);
    setPlusOneLimit(limit);

    if (status === "confirmed") {
      if (limit > 0) {
        goToManageRsvp(matchedRsvpId);
        return;
      }
      setRsvpStep("already_confirmed");
      return;
    }

    setRsvpStep("confirm");
  };

  const finishRsvp = (message: string) => {
    setRsvpSuccess(message);
    setRsvpStep("done");
  };

  const handleConfirm = async (status: "confirmed" | "declined") => {
    if (!rsvpId || !wedding || !id) return;

    if (phone.trim() && !isValidPhone(phone)) {
      setPhoneError(`Enter a valid phone number (${PHONE_PLACEHOLDER})`);
      return;
    }

    setRsvpLoading(true);
    setRsvpError("");
    setRsvpSuccess("");
    setPhoneError("");

    try {
      const { data } = await confirmRsvp({
        rsvp_id: rsvpId,
        status,
        phone: phone || undefined,
      });

      if (status === "declined") {
        finishRsvp("RSVP recorded. We'll miss you!");
        return;
      }

      if (data.requires_payment) {
        const { data: checkout } = await createCheckout({
          wedding_id: wedding.id,
          rsvp_id: rsvpId,
          payment_type: "ticket",
        });
        window.location.href = checkout.url;
        return;
      }

      if (data.requires_plus_ones) {
        setRsvpStep("plus_ones");
        return;
      }

      finishRsvp("You're confirmed! See you there.");
    } catch (err) {
      setRsvpError(err instanceof Error ? err.message : "RSVP failed");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handlePlusOnes = async (plusOnes: PlusOneEntry[]) => {
    if (!rsvpId || !wedding) return;
    setRsvpLoading(true);
    setRsvpError("");
    try {
      const plusOnePrice = partyTierPrice(wedding);

      if (plusOnePrice > 0) {
        const { data: checkout } = await createCheckout({
          wedding_id: wedding.id,
          rsvp_id: rsvpId,
          payment_type: "plus_ones",
          plus_ones: plusOnes,
        });
        window.location.href = checkout.url;
        return;
      }

      const { addPlusOnes } = await import("../api/rsvp");
      await addPlusOnes(rsvpId, plusOnes);
      goToManageRsvp(rsvpId);
    } catch (err) {
      setRsvpError(err instanceof Error ? err.message : "Failed to add plus-ones");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleSelfPayPlusOnes = async (plusOnes: PlusOneEntry[]) => {
    if (!rsvpId) return;
    setRsvpLoading(true);
    setRsvpError("");
    try {
      const { addPlusOnes } = await import("../api/rsvp");
      const { data: result } = await addPlusOnes(rsvpId, plusOnes);
      goToManageRsvp(rsvpId, result.email_warning);
    } catch (err) {
      setRsvpError(err instanceof Error ? err.message : "Failed to add plus-ones");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleRequestPayment = async () => {
    if (!myRequest || !wedding || !id) return;
    setRsvpLoading(true);
    setRsvpError("");
    try {
      const { data } = await createCheckout({
        wedding_id: id,
        request_id: myRequest.id,
        payment_type: "request",
      });
      window.location.href = data.url;
    } catch (err) {
      setRsvpError(err instanceof Error ? err.message : "Payment failed");
    } finally {
      setRsvpLoading(false);
    }
  };

  const handleRequestProfileIncomplete = () => {
    navigate("/profile");
  };

  if (loading) return <Loading />;
  if (error || !wedding) return <ErrorMessage message={error || "Not found"} />;
  if (!id) return null;

  const guestTicketPrice = tierPriceForGuest(wedding, ticketTier);
  const ghostPrice = ghostTierPrice(wedding);
  const hasGuestTicket = guestTicketPrice > 0;
  const hasGhostTicket = ghostPrice > 0;
  const showPriceBadge = weddingRequiresPayment(wedding);
  const showRequestTab = Boolean(wedding.allow_ghost_seats) && wedding.redacted !== true;
  const isFullPage = wedding.redacted !== true;
  const coupleNames = [wedding.couple_name_1, wedding.couple_name_2]
    .filter(Boolean)
    .join(" & ");
  const activeTiers = isFullPage
    ? (wedding.ticket_tiers || []).filter((t) => t.is_active)
    : [];
  const cityState = [wedding.city, wedding.state].filter(Boolean).join(", ");
  const cityLine =
    isFullPage && wedding.zip && cityState
      ? `${cityState} ${wedding.zip}`
      : cityState;
  const venueLine = isFullPage
    ? [wedding.venue_name, wedding.venue_address, cityLine].filter(Boolean).join(" · ")
    : cityLine;

  return (
    <div className="page wedding-detail">
      <div className="wedding-header">
        {isFullPage && wedding.couple_photo_url && (
          <img
            src={wedding.couple_photo_url}
            alt={coupleNames || wedding.title}
            className="couple-photo"
          />
        )}
        <h1>{wedding.title}</h1>
        {coupleNames && <p className="couple-names">{coupleNames}</p>}
        <p className="wedding-date">{formatDate(wedding.date)}</p>
        {venueLine && <p className="wedding-location">{venueLine}</p>}
        {isFullPage && wedding.dress_code && (
          <p className="tier-badge">
            Dress code: {DRESS_CODE_LABELS[wedding.dress_code]}
          </p>
        )}
        {isFullPage && wedding.vibe_tags && wedding.vibe_tags.length > 0 && (
          <div className="wedding-card-tags" style={{ justifyContent: "center" }}>
            {wedding.vibe_tags.map((tag) => (
              <span key={tag} className="badge">
                {VIBE_TAG_LABELS[tag]}
              </span>
            ))}
            {wedding.requires_21_plus && <span className="badge">21+</span>}
          </div>
        )}
        {isFullPage && wedding.story && (
          <section className="wedding-prose-section">
            <h2 className="wedding-prose-title">Your Story</h2>
            <ProseBlocks text={wedding.story} className="wedding-description" />
          </section>
        )}
        {isFullPage && wedding.description && (
          <section className="wedding-prose-section">
            <h2 className="wedding-prose-title">Description</h2>
            <ProseBlocks text={wedding.description} className="wedding-description" />
          </section>
        )}
        {isFullPage && showPriceBadge && ghostPrice > 0 && (
          <p className="wedding-price-badge">
            Public Seats from {formatPrice(ghostPrice)}
          </p>
        )}
        {isFullPage && activeTiers.length > 0 && (
          <div className="tiers-list">
            <h3>Ticket Tiers</h3>
            <ul>
              {activeTiers.map((tier) => (
                <li key={tier.id}>
                  <span className="tier-badge">
                    {TICKET_TIER_LABELS[tier.tier_type] || tier.label}
                  </span>
                  {" — "}
                  {formatPrice(tier.price_cents)}
                  {tierTracksCapacity(tier.tier_type) && tier.capacity > 0 && (
                    <span className="form-hint">
                      {" "}
                      ({Math.max(0, tier.capacity - tier.sold_count)} left)
                    </span>
                  )}
                  <p className="form-hint" style={{ margin: "0.35rem 0 0" }}>
                    {TICKET_TIER_DESCRIPTIONS[tier.tier_type]}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="tabs">
        <button
          type="button"
          className={activeTab === "rsvp" ? "tab active" : "tab"}
          onClick={() => setActiveTab("rsvp")}
        >
          RSVP
        </button>
        {showRequestTab && (
          <button
            type="button"
            className={activeTab === "request" ? "tab active" : "tab"}
            onClick={() => setActiveTab("request")}
          >
            Request Invite
          </button>
        )}
      </div>

      <div className="tab-content">
        {activeTab === "rsvp" && (
          <div className="rsvp-section">
            {rsvpStep === "match" && (
              <>
                <p className="form-hint rsvp-mode-hint">
                  Type your name to find your invitation. Plus-ones who were added by a
                  guest can match here even if this wedding is not listed on Discover.
                </p>
                <RsvpForm weddingId={id} onMatch={handleMatch} />
              </>
            )}

            {rsvpStep === "confirm" && (
              <div className="confirm-section">
                <h3>Confirm Your RSVP</h3>
                <p className="form-hint">
                  Choose whether you are attending. Accept RSVP confirms you&apos;ll be
                  there; Decline RSVP lets the couple know you can&apos;t make it.
                </p>
                {rsvpError && <div className="error-message inline">{rsvpError}</div>}

                {ticketTier === "inner_circle_plus_more" && (
                  <p className="form-hint">
                    Your ticket is complimentary
                    {plusOneLimit > 0
                      ? `. You may add up to ${plusOneLimit} plus-one(s) after accepting.`
                      : "."}
                  </p>
                )}

                <div className="form-group">
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder={PHONE_PLACEHOLDER}
                    value={phone}
                    onChange={(e) => {
                      setPhone(formatPhoneInput(e.target.value));
                      setPhoneError("");
                    }}
                  />
                  {phoneError && (
                    <p className="field-error">{phoneError}</p>
                  )}
                </div>

                <div className="button-row">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={rsvpLoading}
                    onClick={() => handleConfirm("confirmed")}
                    aria-label="Accept RSVP — I will attend"
                  >
                    {rsvpLoading ? "Processing..." : "Accept RSVP"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={rsvpLoading}
                    onClick={() => handleConfirm("declined")}
                    aria-label="Decline RSVP — I cannot attend"
                  >
                    Decline RSVP
                  </button>
                </div>

                {(ticketTier === "party_not_presents" || ticketTier === "venue_showcase") &&
                  hasGuestTicket && (
                  <p className="form-hint">
                    Accepting your RSVP will redirect you to Stripe to complete payment
                    ({formatPrice(guestTicketPrice)}).
                  </p>
                )}
              </div>
            )}

            {rsvpStep === "plus_ones" && (
              <PlusOneForm
                limit={plusOneLimit}
                ticketPrice={partyTierPrice(wedding)}
                loading={rsvpLoading}
                error={rsvpError}
                onSubmit={handlePlusOnes}
                allowSelfPay={partyTierPrice(wedding) > 0}
                onSelfPay={handleSelfPayPlusOnes}
                skipLabel="I'll add plus-ones later"
                onSkip={() => rsvpId && goToManageRsvp(rsvpId)}
              />
            )}

            {rsvpStep === "already_confirmed" && (
              <div className="form">
                <h3>RSVP Status</h3>
                <p className="form-hint">
                  You have already RSVP&apos;d to this wedding. Please contact the wedding
                  organizer if you think this is an error.
                </p>
                {rsvpId && plusOneLimit > 0 && (
                  <Link to={`/weddings/${id}/rsvp/${rsvpId}`} className="btn btn-primary">
                    Manage plus-ones
                  </Link>
                )}
              </div>
            )}

            {rsvpStep === "done" && (
              <div className="success-message">
                <h3>{rsvpSuccess}</h3>
                <div className="button-row" style={{ justifyContent: "center" }}>
                  {rsvpId && plusOneLimit > 0 && (
                    <Link to={`/weddings/${id}/rsvp/${rsvpId}`} className="btn btn-primary">
                      Manage plus-ones
                    </Link>
                  )}
                  <Link to="/" className="btn btn-secondary">
                    Back Home
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "request" && showRequestTab && (
          <>
            <RequestForm
              weddingId={id}
              onProfileIncomplete={handleRequestProfileIncomplete}
              onSuccess={() => {
                if (user) {
                  getMyRequest(id).then(({ data }) => setMyRequest(data));
                }
              }}
            />
            {myRequest?.status === "approved" && (
              <div className="form" style={{ marginTop: "1rem" }}>
                <h3>Your request was approved!</h3>
                {rsvpError && <div className="error-message inline">{rsvpError}</div>}
                {hasGhostTicket ? (
                  <>
                    <p className="form-hint">
                      Complete payment ({formatPrice(ghostPrice)}) within 72 hours to secure your seat.
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={rsvpLoading}
                      onClick={handleRequestPayment}
                    >
                      {rsvpLoading ? "Redirecting..." : "Pay for Ticket"}
                    </button>
                  </>
                ) : (
                  <p className="form-hint">You&apos;re all set — no payment required.</p>
                )}
              </div>
            )}
            {myRequest?.status === "pending" && (
              <p className="form-hint">Your request is pending review (expires in 72 hours).</p>
            )}
            {myRequest?.status === "expired" && (
              <p className="form-hint">This request expired. You may submit a new one if seats remain.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WeddingDetail;
